import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  Loader2,
  History,
  Paperclip,
  PlusCircle,
  Compass,
  FileText,
  ChevronDown,
  X as XIcon,
} from 'lucide-react';
import GoalEntry from '../GoalEntry';
import SessionHistory from '../SessionHistory';
import AuditDrawer from '../AuditDrawer';
import { useSmartStartModal } from '../Modal';
import { useProject } from '../../../contexts/ProjectContext';
import { clearOrchestratorContext, clearFullOrchestratorContext, useOrchestratorContext } from '../../../hooks/useOrchestratorContext';
import api from '../../../api';
import PipelineProposalV2 from './PipelineProposalV2';
import ContextReviewBanner from './ContextReviewBanner';
import ProgressTracker from './ProgressTracker';
import PlanStepCard from './PlanStepCard';
import StepDrawer from './StepDrawer';
import ConversationalLaunch from './ConversationalLaunch';
import SmartRouterAssistant from './SmartRouterAssistant';
import type { ConversationalLaunchResponse } from '../../../services/smartStartApi';
import {
  classifySession,
  completeOrchestratorStep,
  ExportBlockedError,
  createSmartStartSession,
  deleteSession,
  getOrchestratorProposal,
  getOrchestratorSession,
  issueResumeLink,
  issueStepDeepLink,
  launchPipeline,
  listPipelines,
  listSessions,
  rejectOrchestratorStep,
  resolveSessionContext,
  saveIntakeSlots,
  selectOrchestratorPipeline,
  SessionRevisionConflictError,
  AttachedDocument,
} from '../../../services/smartStartApi';
import type {
  HydratedOrchestratorSession,
  OrchestratorProposal,
  PlanStep,
  SmartStartPipelineSummary,
  SmartStartSessionRow,
} from '../../../types/smartStart';

type ViewMode = 'entry' | 'assistant' | 'conversation' | 'proposal' | 'active';

const POLL_INTERVAL_MS = 5000;

const SmartStartV2Page: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId: sessionIdParam } = useParams<{ sessionId?: string }>();
  const urlSessionId = sessionIdParam ? parseInt(sessionIdParam, 10) : null;
  const { projects, currentProject, setCurrentProject } = useProject();

  const [pipelines, setPipelines] = useState<SmartStartPipelineSummary[]>([]);
  const [pipelinesLoaded, setPipelinesLoaded] = useState(false);
  const [recentSessions, setRecentSessions] = useState<SmartStartSessionRow[]>([]);

  const [mode, setMode] = useState<ViewMode>('entry');
  const [proposal, setProposal] = useState<OrchestratorProposal | null>(null);
  const [hydrated, setHydrated] = useState<HydratedOrchestratorSession | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [busyStepIndex, setBusyStepIndex] = useState<number | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [stepDrawer, setStepDrawer] = useState<{ stepIndex: number; url: string; reviewMode?: boolean } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showDocsPopover, setShowDocsPopover] = useState(false);
  const [pendingAutoOpenIndex, setPendingAutoOpenIndex] = useState<number | null>(null);
  const [conversationGoal, setConversationGoal] = useState<string | null>(null);
  const [conversationProjectId, setConversationProjectId] = useState<number | null>(null);
  const [assistantGoal, setAssistantGoal] = useState<string | null>(null);
  const [assistantProjectId, setAssistantProjectId] = useState<number | null>(null);
  const [assistantDocs, setAssistantDocs] = useState<AttachedDocument[]>([]);

  const { modal, confirm: modalConfirm } = useSmartStartModal();

  const loadRecent = useCallback(async () => {
    try {
      setRecentSessions(await listSessions());
    } catch (err: any) {
      console.error('Failed to load recent sessions:', err);
    }
  }, []);

  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (didInitialLoad.current) return;
    didInitialLoad.current = true;

    (async () => {
      try {
        setPipelines(await listPipelines());
      } catch (err: any) {
        toast.error(`Failed to load pipelines: ${err?.response?.data?.error || err.message || err}`);
      } finally {
        setPipelinesLoaded(true);
      }
    })();

    loadRecent();
  }, [loadRecent]);

  const projectsRef = useRef(projects);
  const currentProjectIdRef = useRef(currentProject?.id ?? null);
  useEffect(() => { projectsRef.current = projects; }, [projects]);
  useEffect(() => { currentProjectIdRef.current = currentProject?.id ?? null; }, [currentProject?.id]);

  const isSessionLoadingRef = useRef(false);
  const loadSession = useCallback(async (sessionId: number) => {
    isSessionLoadingRef.current = true;
    const hy = await getOrchestratorSession(sessionId);
    setHydrated(hy);
    setActiveIndex(Math.min(hy.current_step_index || 0, Math.max(0, (hy.navigation_plan || []).length - 1)));

    // Sync dropdown to session's project on load so documents attach to the correct project.
    const sessionProjectId = hy.session?.project_id ?? null;
    if (sessionProjectId && currentProjectIdRef.current !== sessionProjectId) {
      const matched = projectsRef.current.find((p) => p.id === sessionProjectId);
      if (matched) {
        setCurrentProject(matched);
      } else if (projectsRef.current.length > 0) {
        // Project not in list — fetch directly
        import('../../../api').then(({ default: api }) => {
          api.get(`/api/projects/${sessionProjectId}`)
            .then(({ data }) => {
              const project = data?.project || data;
              if (project?.id === sessionProjectId) setCurrentProject(project);
            })
            .catch(() => {});
        });
      }
    }

    if (hy.navigation_plan && hy.navigation_plan.length > 0) {
      setMode('active');
    } else if (hy.session.classified_intent) {
      const p = await getOrchestratorProposal(sessionId);
      setProposal(p);
      setMode('proposal');
    } else {
      setMode('entry');
    }
    isSessionLoadingRef.current = false;
    return hy;
  }, [setCurrentProject]);

  // When user switches project dropdown while a session is active, update the session's project_id
  useEffect(() => {
    if (!urlSessionId || !currentProject || isSessionLoadingRef.current) return;
    if (hydrated?.session?.project_id === currentProject.id) return;
    api.patch(`/api/smart-start/v2/sessions/${urlSessionId}/project`, { project_id: currentProject.id })
      .catch(() => {}); // non-critical, silent fail
  }, [currentProject?.id, urlSessionId, hydrated?.session?.project_id]);

  const lastLoadedSessionId = useRef<number | null>(null);
  useEffect(() => {
    if (!urlSessionId) {
      lastLoadedSessionId.current = null;
      return;
    }
    if (lastLoadedSessionId.current === urlSessionId) return;
    lastLoadedSessionId.current = urlSessionId;

    let cancelled = false;
    (async () => {
      try {
        setIsBusy(true);
        await loadSession(urlSessionId);
      } catch (err: any) {
        if (cancelled) return;
        toast.error(`Could not load session: ${err?.response?.data?.error || err.message || err}`);
        lastLoadedSessionId.current = null;
        navigate('/app/smart-start', { replace: true });
      } finally {
        setIsBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlSessionId, navigate, loadSession]);

  useEffect(() => {
    if (!showDocsPopover) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowDocsPopover(false);
    };
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-docs-popover-root]')) return;
      setShowDocsPopover(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [showDocsPopover]);

  useEffect(() => {
    if (!hydrated || mode !== 'active') return;
    const plan = hydrated.navigation_plan || [];
    const drawerOpen = !!stepDrawer;
    const needsPoll = drawerOpen
      || plan.some((s) => s.status === 'in_progress')
      || plan.some((s) => s.verification_summary && (s.verification_summary as any).pending === true);
    if (!needsPoll) return;
    const id = setInterval(async () => {
      try {
        const hy = await getOrchestratorSession(hydrated.session.id);
        setHydrated(hy);
        const nextPlan = hy.navigation_plan || [];
        const stillPending = nextPlan.some((s) => s.status === 'in_progress')
          || nextPlan.some((s) => s.verification_summary && (s.verification_summary as any).pending === true);
        if (!stillPending && !drawerOpen) clearInterval(id);
      } catch (_) {}
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hydrated, mode, stepDrawer]);

  useEffect(() => {
    if (!stepDrawer || !hydrated) return;
    if (stepDrawer.reviewMode) return;
    const step = hydrated.navigation_plan[stepDrawer.stepIndex];
    if (!step) return;
    if (step.status !== 'completed' && step.status !== 'skipped' && step.status !== 'rejected') return;

    if (step.status !== 'completed') {
      setStepDrawer(null);
      return;
    }

    const nextIndex = hydrated.navigation_plan.findIndex(
      (s, i) => i > stepDrawer.stepIndex && s.status !== 'completed' && s.status !== 'skipped'
    );
    if (nextIndex < 0) {
      setStepDrawer(null);
      toast.success(`"${step.label}" completed — plan finished.`);
      return;
    }

    const nextStep = hydrated.navigation_plan[nextIndex];
    const isInlineNext =
      nextStep.target_screen === 'inline_export' || nextStep.target_screen === 'inline_review';
    const hasMissingSlots =
      Array.isArray(nextStep.missing_slots) && nextStep.missing_slots.length > 0;

    if (isInlineNext || hasMissingSlots) {
      setStepDrawer(null);
      setActiveIndex(nextIndex);
      toast.success(
        `"${step.label}" completed — ${
          isInlineNext ? 'next step runs inline' : 'next step needs more info'
        }.`
      );
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const link = await issueStepDeepLink(hydrated.session.id, nextIndex, hydrated.revision);
        if (cancelled) return;
        if (link.inline) {
          setStepDrawer(null);
          setActiveIndex(nextIndex);
          return;
        }
        if (link.url) {
          setActiveIndex(nextIndex);
          setStepDrawer({ stepIndex: nextIndex, url: link.url });
          toast.success(`"${step.label}" done — opening "${nextStep.label}".`);
          try {
            const hy = await getOrchestratorSession(hydrated.session.id);
            if (!cancelled) setHydrated(hy);
          } catch (_) {}
        } else {
          setStepDrawer(null);
          setActiveIndex(nextIndex);
        }
      } catch (err: any) {
        if (cancelled) return;
        setStepDrawer(null);
        setActiveIndex(nextIndex);
        toast.error(
          `"${step.label}" completed, but couldn't open the next step: ${
            err?.response?.data?.error || err.message || err
          }`
        );
      }
    })();
    return () => { cancelled = true; };
  }, [hydrated, stepDrawer]);

  // Every goal first goes through the Smart Router assistant: it answers or
  // suggests/routes. The guided pipeline flow is reachable from there via
  // "Set up a guided workflow instead" (runGuidedWorkflow).
  const handleSubmitGoal = (goal: string, projectId: number | null, docs: AttachedDocument[]) => {
    setAssistantGoal(goal);
    setAssistantProjectId(projectId);
    setAssistantDocs(docs || []);
    setMode('assistant');
  };

  const runGuidedWorkflow = async (goal: string, projectId: number | null, docs: AttachedDocument[]) => {
    if (docs && docs.length > 0) {
      try {
        setIsBusy(true);
        const session = await createSmartStartSession(goal, projectId, docs);
        await classifySession(session.id);
        const p = await getOrchestratorProposal(session.id);
        setProposal(p);
        setMode('proposal');
        navigate(`/app/smart-start/${session.id}`, { replace: true });
      } catch (err: any) {
        toast.error(`Smart Start failed: ${err?.response?.data?.error || err.message || err}`);
      } finally {
        setIsBusy(false);
      }
      return;
    }
    setConversationGoal(goal);
    setConversationProjectId(projectId);
    setMode('conversation');
  };

  const handleUseWorkflow = (goal: string) => {
    runGuidedWorkflow(goal || assistantGoal || '', assistantProjectId, assistantDocs);
  };

  const handleConversationLaunched = async (result: ConversationalLaunchResponse) => {
    if (!result.session_id) {
      toast.error('Smart Router did not return a session.');
      setMode('entry');
      return;
    }
    navigate(`/app/smart-start/${result.session_id}`, { replace: true });
    try {
      const hy = await getOrchestratorSession(result.session_id);
      setHydrated(hy);
      setActiveIndex(Math.min(hy.current_step_index || 0, Math.max(0, hy.navigation_plan.length - 1)));
      setMode('active');
      if (result.deep_link_url && result.target_step_index != null) {
        if (result.upload_followup) {
          toast(result.message || 'Upload a document to continue.', { icon: '📎', duration: 5000 });
        }
        setActiveIndex(result.target_step_index);
        setStepDrawer({ stepIndex: result.target_step_index, url: result.deep_link_url });
      }
    } catch (err: any) {
      toast.error(`Could not open session: ${err?.response?.data?.error || err.message || err}`);
    }
  };

  const handleConversationCancel = () => {
    setConversationGoal(null);
    setConversationProjectId(null);
    setMode('entry');
  };

  const handleLaunchPipeline = async (
    pipelineKey: string,
    pipelineLabel?: string,
    attachedDocs: AttachedDocument[] = [],
  ) => {
    try {
      setIsBusy(true);
      const projectId = currentProjectIdRef.current;
      const result = await launchPipeline(pipelineKey, {
        projectId,
        goalText: pipelineLabel || null,
        attachedDocuments: attachedDocs,
      });
      navigate(`/app/smart-start/${result.session_id}`, { replace: true });
      const hy = await getOrchestratorSession(result.session_id);
      setHydrated(hy);
      setActiveIndex(Math.min(hy.current_step_index || 0, Math.max(0, hy.navigation_plan.length - 1)));
      setMode('active');
      if (result.deep_link_url && result.target_step_index != null) {
        if (result.upload_followup) {
          toast(result.message || 'Upload a document to continue.', { icon: '📎', duration: 5000 });
        }
        setActiveIndex(result.target_step_index);
        setStepDrawer({ stepIndex: result.target_step_index, url: result.deep_link_url });
      }
    } catch (err: any) {
      toast.error(`Could not launch pipeline: ${err?.response?.data?.error || err.message || err}`);
    } finally {
      setIsBusy(false);
    }
  };

  const handleBackToEntry = () => {
    setPendingAutoOpenIndex(null);
    setHydrated(null);
    setProposal(null);
    setConversationGoal(null);
    setConversationProjectId(null);
    setMode('entry');
    navigate('/app/smart-start');
  };

  const reconcileAfterConflict = async (sessionId: number) => {
    toast.error('Another update happened in parallel — reloaded the latest state. Try again.');
    const hy = await getOrchestratorSession(sessionId);
    setHydrated(hy);
    setActiveIndex(Math.min(hy.current_step_index || 0, Math.max(0, hy.navigation_plan.length - 1)));
  };

  const handleResolveContext = async (choice: 'keep_project' | 'use_session') => {
    const sessionId = proposal?.session_id ?? hydrated?.session?.id;
    if (!sessionId) return;
    try {
      await resolveSessionContext(sessionId, choice);
      setProposal((prev) => (prev ? { ...prev, context_review: { has_conflict: false } } : prev));
      setHydrated((prev) => (prev ? { ...prev, context_review: { has_conflict: false } } : prev));
    } catch (err: any) {
      toast.error(`Could not update context: ${err?.response?.data?.error || err.message || err}`);
      throw err;
    }
  };

  const handleConfirmPipeline = async (pipelineKey: string, slotValues: Record<string, string>) => {
    if (!proposal) return;
    try {
      setIsBusy(true);
      const slotsOut = await saveIntakeSlots(proposal.session_id, slotValues);
      const hy = await selectOrchestratorPipeline(proposal.session_id, pipelineKey, slotsOut.revision);
      setHydrated(hy);
      const firstIndex = hy.current_step_index || 0;
      setActiveIndex(firstIndex);
      setMode('active');
    
      const firstStep = hy.navigation_plan?.[firstIndex];
      const isInline =
        firstStep?.target_screen === 'inline_export'
        || firstStep?.target_screen === 'inline_review';
      const hasMissingSlots =
        firstStep && Array.isArray(firstStep.missing_slots) && firstStep.missing_slots.length > 0;
      if (
        firstStep
        && (firstStep.status === 'pending' || firstStep.status === 'ready')
        && !isInline
        && !hasMissingSlots
      ) {
        setPendingAutoOpenIndex(firstIndex);
      }
    } catch (err: any) {
      if (err instanceof SessionRevisionConflictError) {
        await reconcileAfterConflict(proposal.session_id);
      } else {
        toast.error(`Could not build plan: ${err?.response?.data?.error || err.message || err}`);
      }
    } finally {
      setIsBusy(false);
    }
  };


  const openStepInternal = useCallback(async (stepIndex: number, reviewMode: boolean) => {
    if (!hydrated) return;
    const step = hydrated.navigation_plan[stepIndex];
    if (!step) return;
    setPendingAutoOpenIndex(null);
    try {
      setBusyStepIndex(stepIndex);
      const deepLink = await issueStepDeepLink(hydrated.session.id, stepIndex, hydrated.revision);
      if (deepLink.inline) {
        if (reviewMode) {
          toast('This step has no separate screen to re-open — its result is shown above.', { icon: 'ℹ️' });
          return;
        }
        toast('This step runs inline — generating…', { icon: 'ℹ️' });
        const hy = await completeOrchestratorStep(hydrated.session.id, stepIndex, {
          token: 'inline',
          artifact: { refs: [], output_payload: { inline: true, target_screen: step.target_screen } },
          summary: `Inline step "${step.label}" executed from Smart Router.`,
        });
        if (hy) {
          setHydrated(hy);
          setActiveIndex(Math.min(hy.current_step_index || 0, hy.navigation_plan.length - 1));
        }
        return;
      }
      if (deepLink.url) {
        setActiveIndex(stepIndex);
        setStepDrawer({ stepIndex, url: deepLink.url, reviewMode });
        try {
          const hy = await getOrchestratorSession(hydrated.session.id);
          setHydrated(hy);
        } catch (_) {}
      } else {
        toast.error('Could not build deep link for this step.');
      }
    } catch (err: any) {
      if (err instanceof SessionRevisionConflictError) {
        await reconcileAfterConflict(hydrated.session.id);
      } else if (err instanceof ExportBlockedError) {
        try {
          const hy = await getOrchestratorSession(hydrated.session.id);
          setHydrated(hy);
        } catch (_) {}
        toast.error(err.message, { duration: 6000 });
      } else {
        toast.error(`Open failed: ${err?.response?.data?.error || err.message || err}`);
      }
    } finally {
      setBusyStepIndex(null);
    }
  }, [hydrated]);

  const handleOpenStep = useCallback((stepIndex: number) => {
    return openStepInternal(stepIndex, false);
  }, [openStepInternal]);

  useEffect(() => {
    if (pendingAutoOpenIndex === null || !hydrated) return;
    const step = hydrated.navigation_plan[pendingAutoOpenIndex];
    if (!step || step.status === 'completed' || step.status === 'skipped' || step.status === 'rejected') {
      setPendingAutoOpenIndex(null);
      return;
    }
    const timer = setTimeout(() => {
      handleOpenStep(pendingAutoOpenIndex);
      setPendingAutoOpenIndex(null);
    }, 400);
    return () => clearTimeout(timer);
  }, [pendingAutoOpenIndex, hydrated, handleOpenStep]);

  const handleReopenStep = useCallback((stepIndex: number) => {
    return openStepInternal(stepIndex, true);
  }, [openStepInternal]);

  const handleResumeStep = useCallback(async (stepIndex: number) => {
    if (!hydrated) return;
    setPendingAutoOpenIndex(null);
    try {
      setBusyStepIndex(stepIndex);
      const deepLink = await issueResumeLink(hydrated.session.id, stepIndex);
      if (deepLink.url) {
        setActiveIndex(stepIndex);
        setStepDrawer({ stepIndex, url: deepLink.url });
      } else {
        toast.error('Could not build a resume link for this step.');
      }
    } catch (err: any) {
      toast.error(`Resume failed: ${err?.response?.data?.error || err.message || err}`);
    } finally {
      setBusyStepIndex(null);
    }
  }, [hydrated]);

  const handleRejectStep = useCallback(async (stepIndex: number, reason: string) => {
    if (!hydrated) return;
    setPendingAutoOpenIndex(null);
    try {
      setBusyStepIndex(stepIndex);
      const hy = await rejectOrchestratorStep(hydrated.session.id, stepIndex, reason, hydrated.revision);
      setHydrated(hy);
    } catch (err: any) {
      if (err instanceof SessionRevisionConflictError) {
        await reconcileAfterConflict(hydrated.session.id);
      } else {
        toast.error(`Reject failed: ${err?.response?.data?.error || err.message || err}`);
      }
    } finally {
      setBusyStepIndex(null);
    }
  }, [hydrated]);

  const handleOpenSession = (id: number) => {
    navigate(`/app/smart-start/${id}`);
  };

  const handleDeleteSession = async (id: number) => {
    const ok = await modalConfirm({
      title: 'Delete this Smart Start session?',
      description:
        'This will remove the session, all its step results, and any generated artifacts. This cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete session',
      cancelLabel: 'Keep',
    });
    if (!ok) return;
    try {
      await deleteSession(id);
      await loadRecent();
      if (hydrated?.session.id === id) {
        handleBackToEntry();
      }
      // Clear orchestrator sessionStorage so navigating to Process Documentation
      // after deleting this session doesn't trigger a stale project auto-switch
      const orchCtx = (() => { try { return JSON.parse(sessionStorage.getItem('jarvis_orchestrator_ctx') || '{}'); } catch { return {}; } })();
      if (orchCtx?.sessionId && String(orchCtx.sessionId) === String(id)) {
        clearFullOrchestratorContext();
      }
      toast.success('Session deleted.');
    } catch (err: any) {
      toast.error(`Delete failed: ${err.message || err}`);
    }
  };

  const activeStep = useMemo<PlanStep | null>(() => {
    if (!hydrated) return null;
    return hydrated.navigation_plan[activeIndex] || null;
  }, [hydrated, activeIndex]);

  const sessionIsComplete = hydrated?.session.status === 'completed'
    || (!!hydrated
      && hydrated.navigation_plan.length > 0
      && hydrated.navigation_plan.every((p) => p.status === 'completed' || p.status === 'skipped'));

  const isHydratingExistingSession = urlSessionId != null
    && (!hydrated
        || hydrated.session.id !== urlSessionId);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-6">
      <div className="mx-auto mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-lavender/10 rounded-xl">
            <Compass className="w-5 h-5 text-lavender" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Smart Router</h1>
            <p className="text-sm text-gray-600">
              Describe your outcome — Jarvis builds a plan and walks you through your existing Jarvis screens.
            </p>
          </div>
        </div>
        {mode === 'entry' && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {hydrated && (
              <button
                onClick={handleBackToEntry}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border bg-lavender border-lavender text-white hover:bg-lavender-deep transition-all whitespace-nowrap"
              >
                <PlusCircle className="w-4 h-4" />
                New session
              </button>
            )}
            <button
              onClick={() => setShowHistory((p) => !p)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                showHistory
                  ? 'bg-lavender/10 border-lavender/40 text-lavender'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-lavender/40 hover:text-lavender'
              }`}
            >
              <History className="w-4 h-4" />
              History
              {recentSessions.length > 0 && (
                <span className="bg-lavender text-white text-xs px-1.5 py-0.5 rounded-full">
                  {recentSessions.length}
                </span>
              )}
            </button>
          </div>
        )}
      </div>

      {isHydratingExistingSession && (
        <div className="flex flex-col items-center justify-center py-24 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mb-3 text-lavender" />
          <div className="text-sm font-medium text-gray-700">Loading Smart Router session #{urlSessionId}…</div>
          <div className="text-xs text-gray-500 mt-1">Fetching your plan and last progress.</div>
        </div>
      )}

      {!isHydratingExistingSession && mode === 'entry' && (
        <div className="space-y-4">
          {showHistory && recentSessions.length > 0 && (
            <SessionHistory
              sessions={recentSessions}
              onOpen={handleOpenSession}
              onDelete={handleDeleteSession}
            />
          )}
          {showHistory && recentSessions.length === 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-sm text-gray-500 text-center">
              No past Smart Router sessions yet.
            </div>
          )}
          {!pipelinesLoaded ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading pipelines…
            </div>
          ) : (
            <GoalEntry
              isSubmitting={isBusy}
              onSubmit={handleSubmitGoal}
              onPipelineLaunch={handleLaunchPipeline}
            />
          )}
        </div>
      )}

      {!isHydratingExistingSession && mode === 'assistant' && assistantGoal !== null && (
        <SmartRouterAssistant
          initialMessage={assistantGoal}
          projectId={assistantProjectId}
          onUseWorkflow={handleUseWorkflow}
          onBack={handleBackToEntry}
        />
      )}

      {!isHydratingExistingSession && mode === 'conversation' && conversationGoal && (
        <ConversationalLaunch
          goalText={conversationGoal}
          projectId={conversationProjectId}
          onLaunched={handleConversationLaunched}
          onCancel={handleConversationCancel}
        />
      )}

      {!isHydratingExistingSession && mode === 'proposal' && proposal && (
        <>
          {proposal.context_review?.has_conflict && (
            <ContextReviewBanner
              review={proposal.context_review}
              onResolve={handleResolveContext}
            />
          )}
          <PipelineProposalV2
            proposal={proposal}
            isSubmitting={isBusy}
            onBack={handleBackToEntry}
            onConfirm={handleConfirmPipeline}
          />
        </>
      )}

      {!isHydratingExistingSession && mode === 'active' && hydrated && (
        <div className="mx-auto">
          {hydrated.context_review?.has_conflict && (
            <ContextReviewBanner
              review={hydrated.context_review}
              onResolve={handleResolveContext}
            />
          )}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBackToEntry}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Start a new session
            </button>
            <div className="flex items-center gap-2">
              {Array.isArray(hydrated.session.metadata?.attached_documents)
                && hydrated.session.metadata.attached_documents.length > 0 && (
                  <div className="relative" data-docs-popover-root>
                    <button
                      type="button"
                      onClick={() => setShowDocsPopover((v) => !v)}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-lg transition ${
                        showDocsPopover
                          ? 'bg-sky-100 border-sky-300 text-sky-900'
                          : 'bg-sky-50 border-sky-200 text-sky-800 hover:bg-sky-100'
                      }`}
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      <span>
                        <span className="font-bold">
                          {hydrated.session.metadata.attached_documents.length}
                        </span>{' '}
                        document
                        {hydrated.session.metadata.attached_documents.length === 1 ? '' : 's'} attached
                      </span>
                      <ChevronDown
                        className={`w-3 h-3 transition-transform ${
                          showDocsPopover ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {showDocsPopover && (
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-sky-200 z-30 overflow-hidden">
                        <div className="flex items-start justify-between gap-2 px-4 py-3 bg-sky-50 border-b border-sky-200">
                          <div>
                            <div className="text-xs font-bold text-sky-900 uppercase tracking-wide">
                              Session documents
                            </div>
                            <div className="text-[11px] text-sky-700 mt-0.5">
                              Available to every step in this session.
                            </div>
                          </div>
                          <button
                            onClick={() => setShowDocsPopover(false)}
                            className="p-1 text-sky-700 hover:text-sky-900 hover:bg-sky-100 rounded"
                            aria-label="Close"
                          >
                            <XIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                          {hydrated.session.metadata.attached_documents.map(
                            (d: any, i: number) => (
                              <li
                                key={`${d.name}-${i}`}
                                className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-sky-50/50"
                              >
                                <FileText className="w-4 h-4 text-sky-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-gray-900 truncate font-medium">
                                    {d.name}
                                  </div>
                                  <div className="text-[10px] text-gray-500">
                                    {typeof d.size === 'number'
                                      ? d.size < 1024
                                        ? `${d.size} B`
                                        : d.size < 1024 * 1024
                                          ? `${Math.round(d.size / 1024)} KB`
                                          : `${(d.size / (1024 * 1024)).toFixed(1)} MB`
                                      : ''}
                                    {typeof d.text === 'string' && d.text.length > 0
                                      ? ` · ${(d.text.length / 1000).toFixed(1)}k chars`
                                      : ''}
                                  </div>
                                </div>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              <button
                onClick={() => setAuditOpen(true)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 bg-white border border-gray-200 rounded-lg"
              >
                <History className="w-4 h-4" />
                Audit trail
              </button>
            </div>
          </div>

          {sessionIsComplete && (
            <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl mb-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-emerald-900">Plan complete</div>
                <p className="text-sm text-emerald-800">
                  Every step has been completed. Review artifacts above, or start a new session.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
            <div className="lg:sticky lg:top-6 self-start">
              <ProgressTracker
                plan={hydrated.navigation_plan}
                activeIndex={activeIndex}
                onSelectStep={setActiveIndex}
              />
            </div>
            <div className="min-w-0">
              {activeStep ? (
                <PlanStepCard
                  sessionId={hydrated.session.id}
                  step={activeStep}
                  stageRun={hydrated.stageRuns.find((r) => r.stage_index === activeIndex) || null}
                  isLast={activeIndex === hydrated.navigation_plan.length - 1}
                  isBusy={isBusy || busyStepIndex === activeIndex}
                  canResume={
                    activeStep.status === 'in_progress'
                    && !!hydrated.resume_state
                    && hydrated.resume_state.step_index === activeIndex
                  }
                  attachedDocuments={
                    Array.isArray(hydrated.session.metadata?.attached_documents)
                      ? hydrated.session.metadata.attached_documents
                      : []
                  }
                  clarifyingSlots={hydrated.session.pipeline_snapshot?.clarifying_slots || []}
                  filledSlots={hydrated.intake_slots || {}}
                  onOpenStep={handleOpenStep}
                  onReopenStep={handleReopenStep}
                  onResumeStep={handleResumeStep}
                  onRejectStep={handleRejectStep}
                  onVerificationUpdate={async () => {
                    const hy = await getOrchestratorSession(hydrated.session.id);
                    setHydrated(hy);
                  }}
                />
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 text-gray-500">
                  No step selected.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AuditDrawer
        open={auditOpen}
        sessionId={hydrated?.session.id || null}
        onClose={() => setAuditOpen(false)}
      />

      <StepDrawer
        open={!!stepDrawer && !!hydrated}
        url={stepDrawer?.url || null}
        stepLabel={
          (stepDrawer && hydrated?.navigation_plan[stepDrawer.stepIndex]?.label) || ''
        }
        stepIndexHuman={(stepDrawer?.stepIndex ?? 0) + 1}
        totalSteps={hydrated?.navigation_plan.length || 0}
        onClose={() => setStepDrawer(null)}
      />

      {modal}
    </div>
  );
};

export default SmartStartV2Page;
